import { supabase } from '@/integrations/supabase/client';

/**
 * Get a public URL for a storage image with optional resizing via Supabase Image Transformations.
 * Uses render endpoint for resized images, standard endpoint for originals.
 */
export function getImageUrl(
  storagePath: string,
  options?: { width?: number; height?: number }
): string {
  if (options?.width || options?.height) {
    const baseUrl = supabase.storage.from('car-images').getPublicUrl(storagePath).data.publicUrl;
    // Use Supabase render endpoint for transformations
    const renderUrl = baseUrl.replace(
      '/storage/v1/object/public/',
      '/storage/v1/render/image/public/'
    );
    const params = new URLSearchParams();
    if (options.width) params.set('width', String(options.width));
    if (options.height) params.set('height', String(options.height));
    params.set('resize', 'cover');
    return `${renderUrl}?${params.toString()}`;
  }
  return supabase.storage.from('car-images').getPublicUrl(storagePath).data.publicUrl;
}

interface CarImageData {
  storage_path: string;
  storage_path_avif?: string | null;
  storage_path_webp?: string | null;
  position: number;
}

/**
 * Prefetch car gallery images into the browser cache using new Image().
 * Limits concurrency to avoid saturating the network.
 */
export function prefetchCarImages(
  images: CarImageData[],
  size: { width: number; height: number } = { width: 1200, height: 900 },
  maxConcurrent = 4
): void {
  const sorted = [...images].sort((a, b) => a.position - b.position);
  const toLoad = sorted.slice(0, maxConcurrent);

  toLoad.forEach((img) => {
    // Prefer avif > webp > original
    const url = img.storage_path_avif
      ? getImageUrl(img.storage_path_avif, size)
      : img.storage_path_webp
        ? getImageUrl(img.storage_path_webp, size)
        : getImageUrl(img.storage_path, size);

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
  });
}

/**
 * Preload a single image into the browser cache via new Image().
 * Returns a promise that resolves when loaded (or rejects on error).
 */
export function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Get the best URL for an image entry (avif > webp > original).
 */
export function getBestImageUrl(
  img: CarImageData,
  size?: { width?: number; height?: number }
): string {
  const path = img.storage_path_avif || img.storage_path_webp || img.storage_path;
  return getImageUrl(path, size);
}
